// LIBRERIE/IMPORTS
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

// LIBRERIE
import he from "he";

// HOOKS
import useArticoli, { ArticoloBlog } from '../../hooks/useGetArticoli';
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";

// CONFIGURAZIONI
import { appConfig } from "../../config/appConfig";

// COMPONENTI
import WidgetPartners from "../Widgets/WidgetPartners/WidgetPartners";
import WidgetContacts from "../Widgets/WidgetContacts/WidgetContacts";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import NotFound from "../Error/NotFound";
import Pagination from '../Properties/Pagination';
import SubHeaderDefault from '../Header/subHeader/SubHeaderDefault';

const formatDate = (dateString: string | null, t: (key: string) => string): string => {
    if (!dateString) return t('blog.date.unavailable');
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return t('blog.date.invalid');
        }
        return date.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch (e) {
        console.error("Error formatting date:", dateString, e);
        return t('blog.date.invalid');
    }
};

const LoadingSpinner: React.FC = () => {
    const { t } = useTranslation();
    return (
        <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
                <span className="sr-only">{t('blog.loading')}...</span>
            </div>
            <p>{t('blog.loadingArticles')}</p>
        </div>
    );
};

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => {
    const { t } = useTranslation();
    return (
        <div className="alert alert-danger" role="alert">
            {t('blog.errorMessage')} {message}
        </div>
    );
};

interface BlogProps {
    agencyId: number;
}

const Blog: React.FC<BlogProps> = ({ agencyId }) => {
    const { t } = useTranslation();
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);
    const { articles, isLoading, error, pagination, fetchArticoli } = useArticoli({
        agencyId: agencyId,
        initialLimit: 5,
    });

    const allPartnersForWidget = appConfig.content.partners;

    const handlePageChange = (page: number) => { fetchArticoli(page); };

    if (agencyLoading) return <LoadingIndicator />;
    if (agencyError) return <NotFound message={t('blog.loadingError')} />;

    // --- Preparazione Dati e Testi Tradotti ---
    const agencyName = agency?.agencyName ?? t('common.defaultAgencyName');
    const pageTitle = t('blogPage.pageTitle');
    const helmetTitle = t('common.pageTitlePattern', { pageTitle: pageTitle, agencyName: agencyName });

    const renderArticle = (article: ArticoloBlog) => {
        const detailUrl = `/blog/${article.id}`;
        const imageUrl = article.foto ? `https://www.casagest24.it/${article.foto}` : '/images/blog/placeholder.jpg';

        return (
            <Link to={detailUrl} className="mb-3 blog-card" key={article.id}>
                <div className="row m-0">
                    <div className="col-12 col-md-4 col-xl-5 p-0">
                        <div className="img-wrapper">
                            <img src={imageUrl} alt={`${article.titolo || t('blog.article.imageAltFallback')}`} />
                        </div>
                    </div>
                    <div className="col-12 col-md-8 col-xl-7 pt-3 pt-lg-5">
                        {article.nomeCategoria && (
                            <div className="blog-post-category mb-2">
                                <strong>{article.nomeCategoria}</strong>
                            </div>
                        )}
                        <h4 dangerouslySetInnerHTML={{ __html: he.decode(article.titolo) }} />
                        <p className="mb-2" dangerouslySetInnerHTML={{ __html: he.decode(article.testoRidotto || t('blog.article.noShortDescription')) }} />
                        <div className="public-data">
                            <i className="far fa-clock mr-1"></i>
                            <span>{formatDate(article.dataPubblicazione, t)}</span>
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    return (
        <>
            <Helmet>
                <title>{helmetTitle}</title>
            </Helmet>

            <SubHeaderDefault title={t('blogPage.pageTitle')} isSeo={true} />

            <section className="space-ptb">
                <div className="container-fluid">
                    <div className="row justify-content-around">
                        <div className="col-12 col-lg-8 mb-4 mb-lg-0">
                            <div className="section-title">
                                <h2>{t('blogPage.articlesTitle')}</h2>
                            </div>
                            {isLoading && <LoadingSpinner />}
                            {error && <ErrorMessage message={error} />}
                            {!isLoading && !error && articles.length === 0 && (
                                <p>{t('blogPage.notFoundMessage')}</p>
                            )}
                            {!isLoading && !error && articles.length > 0 && (
                                <>
                                    {articles.map(renderArticle)}
                                    <Pagination
                                        count={pagination.totalItems} // Passa il numero totale di articoli
                                        limit={pagination.limit} // Passa il limite di articoli per pagina
                                        currentPage={pagination.currentPage} // Passa la pagina corrente
                                        onPageChange={handlePageChange} // Passa la funzione di callback
                                        siblingCount={1} // Quanti "siblings" ci sono [ Es. {1} ]--> { 1 ... 4 [5] 6 ... 43 }
                                    />
                                </>
                            )}
                        </div>
                        <div className="col-12 col-lg-4 col-xl-3 mb-4 sidebar">
                            <WidgetContacts
                                agencyDataAddress={agency?.address_details?.address ?? ''}
                                agencyDataCity={agency?.address_details?.city_name ?? ''}
                                agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                                agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                agencyDataEmail={appConfig.agency.agencyEmail}
                                agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                                agencyDataPhone1={agency?.phone1 ?? ''}
                                agencyDataPhone2={agency?.phone2 ?? ''}
                                agencyDataInternationalPrefix={appConfig.agency.intlPrefix ?? '+39'}
                                agencyDataMobile={agency?.mobile ?? ''}
                                agencyDataMobile2={agency?.mobile2 ?? ''}
                                agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                                agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                                agencyDataFacebook={appConfig.socialLinks.facebook}
                                agencyDataInstagram={appConfig.socialLinks.instagram}
                                agencyDataTwitter={appConfig.socialLinks.twitter}
                                agencyDataLinkedin={appConfig.socialLinks.linkedin}
                                agencyDataPinterest={appConfig.socialLinks.pinterest}
                                agencyDataYoutube={appConfig.socialLinks.youtube}
                            />
                            <WidgetPartners partners={allPartnersForWidget} />
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Blog;